import { CommercialApiConfig } from '@/services/CommercialApiConfig';
import { useFetch } from '@/composables/UseFetch';
import type {
  CompanyItem, StateItem, LobItem, IssueCodeItem, KeyFieldItem,
  HoverOption, HoverSearchRequest, AddHoverRequest, AddHoverResponse,
  ValidateRequestCodeResponse, HoverImpactRequest, HoverImpactResponse,
  GroupItem, GroupRequest, UpdateGroupRequest, GroupOperationResponse,
  UserProfile, AssignUserRequest, UpdateGroupMembersRequest, MergeGroupsRequest,
  MenuItem, MenuItemRequest, MenuOperationResponse,
} from '@/models/TableMaintenanceViewModels';

type TableMaintenanceGetFn = <T>(url: string) => Promise<T>;
type TableMaintenanceSendFn = <T>(method: string, url: string, body?: unknown) => Promise<T>;

export function useTableMaintenanceApi(
  getImpl?: TableMaintenanceGetFn,
  sendImpl?: TableMaintenanceSendFn,
) {
  const base = () => `${CommercialApiConfig.endpoint}/table-maintenance`;
  const { fetchResilientWithAuth } = useFetch();

  /**
   * Build a human-readable error from a failed response, preferring the API's
   * own message. The table-maintenance endpoints return business-rule failures
   * (e.g. "Group 'X' must exist in PMSPMM01.", "Group 'X' already exists.") as a
   * non-2xx status with a JSON body shaped like { success, message } or
   * { error }. Surface that text instead of a generic "failed with status 400"
   * line so the UI can show the real reason.
   */
  const errorFromResponse = async (response: Response, method: string, url: string): Promise<Error> => {
    const fallback = `${method.toUpperCase()} ${url} failed with status ${response.status} ${response.statusText}`;
    let serverMessage = '';
    try {
      const text = (await response.text()).trim();
      if (text) {
        try {
          const parsed = JSON.parse(text);
          serverMessage = parsed?.message || parsed?.error || parsed?.title
            || (typeof parsed === 'string' ? parsed : '');
        } catch {
          serverMessage = text; // non-JSON body — use the raw text
        }
      }
    } catch (e) {
      // response.text() can reject on a mid-stream network failure; the status
      // line is still a useful fallback, but log so it's traceable in dev.
      console.warn(`Could not read error body for ${method} ${url}`, e);
    }

    return new Error(serverMessage || fallback);
  };

  const defaultGet: TableMaintenanceGetFn = async <T>(url: string) => {
    const response = await fetchResilientWithAuth(url, { method: 'GET' });

    if (!response.ok) {
      throw await errorFromResponse(response, 'GET', url);
    }

    const data = (await response.json()) as T;
    return data;
  };

  const defaultSend: TableMaintenanceSendFn = async <T>(
    method: string,
    url: string,
    body?: unknown,
  ) => {
    const init: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    };

    const response = await fetchResilientWithAuth(url, init);

    if (!response.ok) {
      throw await errorFromResponse(response, method, url);
    }

    const data = (await response.json()) as T;
    return data;
  };

  const get: TableMaintenanceGetFn = <T>(url: string) => {
    const effectiveGet = (getImpl ?? defaultGet) as TableMaintenanceGetFn;
    return effectiveGet<T>(url);
  };

  const send: TableMaintenanceSendFn = <T>(method: string, url: string, body?: unknown) => {
    const effectiveSend = (sendImpl ?? defaultSend) as TableMaintenanceSendFn;
    return effectiveSend<T>(method, url, body);
  };

  // ── Lookups ───────────────────────────────────────────────────────────

  function getCompanies(): Promise<CompanyItem[]> {
    return get<CompanyItem[]>(`${base()}/companies`);
  }

  function getStates(loc: string, mco: string, pco: string): Promise<StateItem[]> {
    const params = new URLSearchParams({ loc, mco, pco });
    return get<StateItem[]>(`${base()}/states?${params}`);
  }

  function getLobs(loc: string, mco: string, pco: string, state: string): Promise<LobItem[]> {
    const params = new URLSearchParams({ loc, mco, pco, state });
    return get<LobItem[]>(`${base()}/lobs?${params}`);
  }

  function getIssueCodes(): Promise<IssueCodeItem[]> {
    return get<IssueCodeItem[]>(`${base()}/issue-codes`);
  }

  function getKeyFields(
    loc: string, mco: string, pco: string, state: string, lob: string,
    type: string, fileName = '', keyFld2 = '', keyFld3 = '', keyFld4 = '', coverageLevel = ''
  ): Promise<KeyFieldItem[]> {
    const params = new URLSearchParams({
      loc, mco, pco, state, lob, type, fileName, keyFld2, keyFld3, keyFld4, coverageLevel,
    });
    return get<KeyFieldItem[]>(`${base()}/hovers/key-fields?${params}`);
  }

  // ── Hovers ────────────────────────────────────────────────────────────

  function searchHovers(request: HoverSearchRequest): Promise<HoverOption[]> {
    return send<HoverOption[]>('POST', `${base()}/hovers/search`, request);
  }

  function searchUserHovers(request: HoverSearchRequest & { userName: string }): Promise<HoverOption[]> {
    return send<HoverOption[]>('POST', `${base()}/hovers/user-search`, request);
  }

  function addHover(request: AddHoverRequest): Promise<AddHoverResponse> {
    return send<AddHoverResponse>('POST', `${base()}/hovers`, request);
  }

  function validateRequestCode(requestCode: string): Promise<ValidateRequestCodeResponse> {
    return get<ValidateRequestCodeResponse>(`${base()}/hovers/validate-request-code/${enc(requestCode)}`);
  }

  function getHoverImpact(request: HoverImpactRequest): Promise<HoverImpactResponse> {
    return send<HoverImpactResponse>('POST', `${base()}/hovers/impact`, request);
  }

  // ── Groups ────────────────────────────────────────────────────────────

  function getGroups(appContext: string): Promise<GroupItem[]> {
    const params = new URLSearchParams({ appContext });
    return get<GroupItem[]>(`${base()}/groups?${params}`);
  }

  function addGroup(request: GroupRequest): Promise<GroupOperationResponse> {
    return send<GroupOperationResponse>('POST', `${base()}/groups`, request);
  }

  function updateGroup(request: UpdateGroupRequest): Promise<GroupOperationResponse> {
    return send<GroupOperationResponse>('PUT', `${base()}/groups`, request);
  }

  function deleteGroup(groupName: string, appContext: string, user: string): Promise<GroupOperationResponse> {
    const params = new URLSearchParams({ appContext, user });
    return send<GroupOperationResponse>('DELETE', `${base()}/groups/${enc(groupName)}?${params}`);
  }

  // ── User-Group Assignment ─────────────────────────────────────────────

  function getAllUsers(appContext: string): Promise<UserProfile[]> {
    const params = new URLSearchParams({ appContext });
    return get<UserProfile[]>(`${base()}/users?${params}`);
  }

  function getGroupUsers(groupName: string, appContext: string): Promise<UserProfile[]> {
    const params = new URLSearchParams({ appContext });
    return get<UserProfile[]>(`${base()}/groups/${enc(groupName)}/users?${params}`);
  }

  function assignUserToGroup(request: AssignUserRequest): Promise<GroupOperationResponse> {
    return send<GroupOperationResponse>('POST', `${base()}/groups/${enc(request.groupName)}/users`, request);
  }

  function removeUserFromGroup(groupName: string, userId: string, appContext: string, user: string): Promise<GroupOperationResponse> {
    const params = new URLSearchParams({ appContext, user });
    return send<GroupOperationResponse>('DELETE', `${base()}/groups/${enc(groupName)}/users/${enc(userId)}?${params}`);
  }

  function updateGroupMembers(request: UpdateGroupMembersRequest): Promise<GroupOperationResponse> {
    return send<GroupOperationResponse>('PUT', `${base()}/groups/${enc(request.groupName)}/members`, request);
  }

  function mergeGroups(request: MergeGroupsRequest): Promise<GroupOperationResponse> {
    return send<GroupOperationResponse>('POST', `${base()}/groups/merge`, request);
  }

  // ── Menu Items ────────────────────────────────────────────────────────

  function getMenuItems(appContext: string): Promise<MenuItem[]> {
    const params = new URLSearchParams({ appContext });
    return get<MenuItem[]>(`${base()}/menu-items?${params}`);
  }

  function addMenuItem(request: MenuItemRequest): Promise<MenuOperationResponse> {
    return send<MenuOperationResponse>('POST', `${base()}/menu-items`, request);
  }

  function updateMenuItem(request: MenuItemRequest): Promise<MenuOperationResponse> {
    return send<MenuOperationResponse>('PUT', `${base()}/menu-items`, request);
  }

  function deleteMenuItem(menuId: string, sequence: number, appContext: string, user: string): Promise<MenuOperationResponse> {
    const params = new URLSearchParams({ appContext, user });
    return send<MenuOperationResponse>('DELETE', `${base()}/menu-items/${enc(menuId)}/${sequence}?${params}`);
  }

  // ── util ──────────────────────────────────────────────────────────────

  function enc(v: string) { return encodeURIComponent(v); }

  return {
    getCompanies, getStates, getLobs, getIssueCodes, getKeyFields,
    searchHovers, searchUserHovers, addHover, validateRequestCode, getHoverImpact,
    getGroups, addGroup, updateGroup, deleteGroup,
    getAllUsers, getGroupUsers, assignUserToGroup, removeUserFromGroup, updateGroupMembers, mergeGroups,
    getMenuItems, addMenuItem, updateMenuItem, deleteMenuItem,
  };
}
